/**
 * A call transcript in chat style: the bot on the right, the callee on the
 * left, detected sounds (a beep, a fax tone) centered. Used for the live
 * transcript during a call and for past calls in the history.
 */
import { ReactNode } from 'react';

export interface TranscriptLine {
  /** Already formatted for display, e.g. a local time. */
  timestamp?: string;
  // 'event' marks a non-speech sound the bot detected: a beep or a fax tone,
  // or something the bot did: hung up at the time limit ('time_limit').
  role: 'user' | 'assistant' | 'event';
  /** Which event, for role 'event'. */
  event?: string;
  content: string;
}

/** One credit buys a call of up to this long; a call can be extended to twice that. */
export const CALL_MINUTES_PER_CREDIT = 4;
export const MAX_CALL_MINUTES = 8;

/** Whether the bot had to hang up at the call's time limit. */
export function wasCutOffAtTimeLimit(lines: { event?: string }[]): boolean {
  return lines.some((line) => line.event === 'time_limit');
}

/**
 * The warning on a transcript whose call reached its time limit: the business
 * may not be finished, the agent said goodbye rather than dropping the line,
 * and a longer limit is available for a call back.
 */
export function TimeLimitBanner({ limitMinutes = CALL_MINUTES_PER_CREDIT }: { limitMinutes?: number }) {
  return (
    <div
      role="alert"
      style={{
        margin: '0 0 14px 0',
        padding: '12px 14px',
        borderRadius: '8px',
        backgroundColor: 'rgba(251, 191, 36, 0.18)',
        border: '1px solid #f59e0b',
        color: '#7c2d12',
        textAlign: 'left',
        fontSize: '14px',
        lineHeight: '1.5',
      }}
    >
      <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>
        ⚠️ Reached the {limitMinutes}-minute time limit
      </div>
      The call ran out of time before the goal was confirmed. The agent apologized and said goodbye before
      hanging up, so it did not end abruptly for the other person.{' '}
      {limitMinutes < MAX_CALL_MINUTES ? (
        <>
          If the matter is not settled, consider calling back with the maximum call length extended to{' '}
          {MAX_CALL_MINUTES} minutes. The second credit is charged only if the call runs longer than{' '}
          {CALL_MINUTES_PER_CREDIT} minutes.
        </>
      ) : (
        <>If the matter is not settled, consider calling back, perhaps splitting it across two calls.</>
      )}
    </div>
  );
}

interface TranscriptProps {
  messages: TranscriptLine[];
  /** Shown instead of the bubbles while there is nothing to show. */
  empty: ReactNode;
  /** Scrolled into view as new lines arrive (live transcript). */
  endRef?: React.RefObject<HTMLDivElement | null>;
  maxHeight?: string;
}

export function Transcript({ messages, empty, endRef, maxHeight = '400px' }: TranscriptProps) {
  return (
    <div style={{ maxHeight, overflowY: 'auto', padding: '8px 0' }}>
      {messages.length > 0 ? (
        messages.map((msg, index) =>
          msg.role === 'event' ? (
            <div
              key={index}
              style={{
                textAlign: 'center',
                fontSize: '14px',
                color: '#666',
                fontStyle: 'italic',
                marginBottom: '14px',
              }}
            >
              {msg.content}
              {msg.timestamp && ` · ${msg.timestamp}`}
            </div>
          ) : (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'assistant' ? 'flex-end' : 'flex-start',
                marginBottom: '14px',
              }}
            >
              <div
                style={{
                  maxWidth: '80%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: msg.role === 'assistant' ? '#1a1a1a' : '#f0f0f0',
                  border: msg.role === 'assistant' ? '1px solid #333' : '1px solid #ccc',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    color: msg.role === 'assistant' ? '#aaa' : '#666',
                    marginBottom: '4px',
                    fontWeight: '600',
                    textAlign: msg.role === 'assistant' ? 'right' : 'left',
                  }}
                >
                  {msg.role === 'assistant' ? 'Callout agent' : 'Callee'}
                  {msg.timestamp && ` · ${msg.timestamp}`}
                </div>
                <div
                  style={{
                    fontSize: '17px',
                    color: msg.role === 'assistant' ? '#ffffff' : '#111111',
                    lineHeight: '1.5',
                    textAlign: 'left',
                  }}
                >
                  {msg.content}
                </div>
              </div>
            </div>
          )
        )
      ) : (
        <div
          style={{
            fontSize: '15px',
            color: '#666',
            fontStyle: 'italic',
            textAlign: 'center',
            padding: '20px 0',
          }}
        >
          {empty}
        </div>
      )}
      {endRef && <div ref={endRef} />}
    </div>
  );
}

/** The white card the transcript sits in, in both the live and the past view. */
export function TranscriptCard({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div
      style={{
        margin: '20px 0',
        padding: '16px 20px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #d0d0d0',
        maxWidth: '600px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#111' }}>{title}</h3>
      {children}
    </div>
  );
}
